import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { FilterListSMSIcon } from '@rsos/assets/icons';
import { Box, Text } from '@rsos/base-components';
import { SOURCE_TCAPI } from '@rsos/constants/locationSources';
import {
  useGetConversation,
  useGetCreateConversation,
  useGetEndConversation,
  useHasConversationButtons,
  useIsConversationAbleToEnd,
  useIsConversationEnded,
} from '@rsos/reduxDataStore/src/features/conversations/utils';
import { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { useCurrentOrgName } from '@rsos/sinatra/src/selectors';
import useDisplayPhoneNumber from '../../../../hooks/useDisplayPhoneNumber';
import useIsVT911Enabled from '../../../../hooks/useIsVT911Enabled';
import { LiveVideoButtonContainer } from '../VideoTo911/LiveVideoButtonContainer';
import {
  HeaderContainer,
  RedCircle,
  StartChatButton,
  StartChatContainer,
} from './ConversationsChatbox.styles';
import EndChatModal from './EndChatModal';
import ExpandButton from './ExpandButton';

interface ConversationsChatHeaderProps {
  isChatBoxVisible: boolean;
  setIsChatBoxVisible?: (isChatBoxVisible: boolean) => void;
  isConversationStarted: boolean;
  handleStartChat?: () => void;
  isForOverview?: boolean;
  isTcapiClosed?: boolean;
}

const ConversationsChatHeader = ({
  isChatBoxVisible,
  setIsChatBoxVisible,
  isConversationStarted,
  handleStartChat,
  isForOverview = false,
  isTcapiClosed: isTcapiClosedProp = false,
}: ConversationsChatHeaderProps) => {
  const isVT911Enabled = useIsVT911Enabled();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const orgName = useCurrentOrgName();

  const callerID = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.selectedCallerID,
  );

  const hasButtons = useHasConversationButtons(callerID);
  const isConversationPotentiallyEnded = useIsConversationEnded(callerID);
  const isAbleToEnd = useIsConversationAbleToEnd(callerID);
  const isConversationEnded =
    isConversationPotentiallyEnded && isConversationStarted;
  const isConversationOngoing =
    !isConversationPotentiallyEnded && isConversationStarted;

  const [fetchCreateConversation, createConversationStates] =
    useGetCreateConversation(callerID);

  const [fetchEndConversation, endConversationStates] =
    useGetEndConversation(callerID);

  const displayPhoneNumber = useDisplayPhoneNumber(callerID);

  const tcapiConversation = useGetConversation(callerID);

  const isTcapiClosedInternal = useMemo(() => {
    if (
      !tcapiConversation ||
      tcapiConversation.source !== SOURCE_TCAPI ||
      !tcapiConversation.data
    ) {
      return false;
    }
    return (
      tcapiConversation.data.acceptStatus === 'timed_out' ||
      tcapiConversation.data.status === 'inactive'
    );
  }, [tcapiConversation]);

  const isTcapiTimedOut = useMemo(() => {
    if (
      !tcapiConversation ||
      tcapiConversation.source !== SOURCE_TCAPI ||
      !tcapiConversation.data
    ) {
      return false;
    }
    return tcapiConversation.data.acceptStatus === 'timed_out';
  }, [tcapiConversation]);

  // Use prop if provided (from Overview tab), otherwise use internal calculation
  const isTcapiClosed = isTcapiClosedProp || isTcapiClosedInternal;

  const isTcapi = tcapiConversation?.source === SOURCE_TCAPI;

  const handleStartConversation = async () => {
    if (fetchCreateConversation) {
      return fetchCreateConversation({ callerID, orgName }).then(() => {
        handleStartChat?.();
        setIsChatBoxVisible?.(true);
        return createConversationStates;
      });
    } else {
      return { isSuccess: false };
    }
  };

  const onStartConversation = async () => {
    if (isConversationOngoing) {
      return { isSuccess: true };
    }

    return handleStartConversation();
  };

  const handleDefaultStartClick = () => {
    if (isConversationOngoing) {
      setIsModalOpen(true);
    } else {
      handleStartConversation();
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalConfirm = () => {
    if (fetchEndConversation) {
      fetchEndConversation({ callerID, orgName });
    }

    setIsModalOpen(false);
  };

  const handleExpandButtonClicked = () => {
    if (setIsChatBoxVisible) {
      setIsChatBoxVisible(!isChatBoxVisible);
    }
  };

  return (
    <HeaderContainer data-name="conversations-chat-header" pb="16px" pt="12px">
      <Box horizontal justify="space-between" px="12px" width="100%">
        <Box>
          <Text inline fontWeight="bold">
            <Text color="primaryText" size="normal">
              {isConversationOngoing && !isTcapiClosed && <RedCircle />}
              {isConversationEnded || isTcapiClosed
                ? 'Closed Chat'
                : 'Live Chat'}
            </Text>
            <Box horizontal>
              <Text color="secondaryText" fontWeight="regular" size="xsmall">
                {displayPhoneNumber}
              </Text>
            </Box>
          </Text>
        </Box>
        {isConversationStarted &&
          !((isForOverview && isTcapiClosed) || isTcapiTimedOut) && (
            <ExpandButton
              isChatBoxVisible={isChatBoxVisible}
              onActionButtonClick={handleExpandButtonClicked}
            />
          )}
      </Box>
      {hasButtons && !isTcapiClosed && (
        <StartChatContainer gridColumnGap="8px">
          {isVT911Enabled && (
            <Box width="50%">
              <LiveVideoButtonContainer
                onStartConversation={onStartConversation}
              />
            </Box>
          )}
          <Box
            alignItems="center"
            justifyContent="center"
            horizontal
            width={isVT911Enabled ? '50%' : '100%'}
          >
            <StartChatButton
              onClick={handleDefaultStartClick}
              disabled={!isAbleToEnd}
              btnType={isConversationOngoing ? 'warning_tertiary' : 'tertiary'}
              isLoadingProp={
                createConversationStates?.isLoading ||
                endConversationStates?.isLoading
              }
            >
              <FilterListSMSIcon width={12} height={12} />
              {isConversationOngoing
                ? 'End Chat'
                : isTcapi
                  ? 'Accept Chat'
                  : 'Start Chat'}
            </StartChatButton>
          </Box>
        </StartChatContainer>
      )}
      <EndChatModal
        isLoading={endConversationStates?.isLoading}
        isOpened={isModalOpen}
        callerID={callerID}
        onClose={handleModalClose}
        onConfirm={handleModalConfirm}
      />
    </HeaderContainer>
  );
};

export default ConversationsChatHeader;
